/**
 * i18n Configuration for Multi-lingual Technician Interface
 * 
 * Supports: English, Arabic, Hindi, Urdu, Bengali, Tagalog, Tamil, Malayalam
 * 
 * Features:
 * - Automatic language detection from user preference
 * - RTL support for Arabic and Urdu
 * - Fallback to English for missing translations
 * - Dynamic activity translations loaded from backend
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enTranslations from './locales/en.json';
import arTranslations from './locales/ar.json';
import hiTranslations from './locales/hi.json';
import urTranslations from './locales/ur.json';
import bnTranslations from './locales/bn.json';
import tlTranslations from './locales/tl.json';
import taTranslations from './locales/ta.json';
import mlTranslations from './locales/ml.json';

// Language metadata
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', direction: 'rtl' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', direction: 'ltr' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', direction: 'ltr' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', direction: 'ltr' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', direction: 'ltr' }
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

// RTL languages
export const RTL_LANGUAGES = ['ar', 'ur'];

// Check if a language is RTL
export const isRTL = (langCode: string): boolean => {
  return RTL_LANGUAGES.includes(langCode);
};

// Get language direction
export const getLanguageDirection = (langCode: string): 'ltr' | 'rtl' => {
  return isRTL(langCode) ? 'rtl' : 'ltr';
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      ar: { translation: arTranslations },
      hi: { translation: hiTranslations },
      ur: { translation: urTranslations },
      bn: { translation: bnTranslations },
      tl: { translation: tlTranslations },
      ta: { translation: taTranslations },
      ml: { translation: mlTranslations }
    },
    fallbackLng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false // React already escapes by default
    },
    
    detection: {
      // Custom detection order - prefer stored preference
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'technician_language',
      caches: ['localStorage']
    },
    
    // React specific options
    react: {
      useSuspense: false
    }
  });

// Function to update document direction for RTL support
export const updateDocumentDirection = (langCode: string) => {
  const direction = getLanguageDirection(langCode);
  document.documentElement.dir = direction;
  document.documentElement.lang = langCode;
  
  // Add/remove RTL class for CSS
  if (direction === 'rtl') {
    document.documentElement.classList.add('rtl');
  } else {
    document.documentElement.classList.remove('rtl');
  }
};

// Listen for language changes
i18n.on('languageChanged', (lng) => {
  updateDocumentDirection(lng);
  localStorage.setItem('technician_language', lng);
});

// Set initial direction
if (i18n.language) {
  updateDocumentDirection(i18n.language);
}

export default i18n;

